import { useState } from "react";
import { GripVertical, MoreHorizontal } from "lucide-react";
import { HIGHLIGHT_COLORS } from "@/hooks/useBoardOrganization";

// A full-width divider <tr>. `colSpan` must match the table's total columns.
export default function BoardDividerRow({
  colSpan, divider, canOrganize, canMove = canOrganize, gripProps, sectionKey,
  onMoveClick, onRename, onDelete,
}) {
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState(divider.label);
  const bg = divider.color ? `color-mix(in srgb, var(--bo-${divider.color}, ${divider.color}) 22%, transparent)` : "#f8fafc";

  const commitRename = () => {
    const clean = labelDraft.trim();
    if (clean && clean !== divider.label) onRename(divider.id, clean, undefined, divider._bo_version);
    setEditing(false);
  };

  return (
    <tr
      className="board-divider-row"
      data-testid={`board-divider-row-${divider.id}`}
      data-board-row-id={divider.id}
      data-board-row-kind="divider"
      data-board-section-key={sectionKey || divider.section_key || ""}
      data-bo-version={divider._bo_version ?? 0}
      style={{ background: bg }}
    >
      <td colSpan={colSpan} style={{ padding: "4px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 26 }}>
          {canMove ? (
            <button
              aria-label="Drag divider to reorder"
              className="board-drag-handle"
              data-testid={`board-divider-drag-${divider.id}`}
              onClick={(event) => { event.stopPropagation(); }}
              style={{
                width: 20, height: 22, display: "inline-flex", alignItems: "center",
                justifyContent: "center", cursor: "grab", border: "none", background: "transparent",
              }}
              type="button"
              {...(gripProps || {})}
            >
              <GripVertical size={14} />
            </button>
          ) : (
            <span style={{ width: 20 }} />
          )}
          {editing ? (
            <input
              autoFocus
              data-testid={`board-divider-label-input-${divider.id}`}
              onBlur={commitRename}
              onChange={(event) => setLabelDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") commitRename();
                if (event.key === "Escape") { setLabelDraft(divider.label); setEditing(false); }
              }}
              style={{ font: "inherit", padding: "2px 6px", flex: 1, maxWidth: 320 }}
              value={labelDraft}
            />
          ) : (
            <strong
              data-testid={`board-divider-label-${divider.id}`}
              onDoubleClick={() => canOrganize && setEditing(true)}
              style={{ fontSize: 11.5, letterSpacing: 0.6, textTransform: "uppercase", cursor: canOrganize ? "text" : "default" }}
            >
              {divider.label}
            </strong>
          )}
          <span style={{ flex: 1 }} />
          {canOrganize && (
            <div style={{ position: "relative" }}>
              <button
                aria-label="Divider actions"
                data-testid={`board-divider-menu-${divider.id}`}
                onClick={(event) => { event.stopPropagation(); setMenuOpen((v) => !v); }}
                style={{ padding: "2px 6px", background: "transparent", border: "none", cursor: "pointer" }}
                type="button"
              >
                <MoreHorizontal size={14} />
              </button>
              {menuOpen && (
                <div
                  data-testid={`board-divider-menu-panel-${divider.id}`}
                  onMouseLeave={() => setMenuOpen(false)}
                  style={{
                    position: "absolute", right: 0, top: "100%", background: "#fff",
                    border: "1px solid #e2e8f0", borderRadius: 8, padding: 6,
                    boxShadow: "0 4px 20px rgba(15,23,42,0.15)", zIndex: 40, minWidth: 180,
                    display: "flex", flexDirection: "column", gap: 4,
                  }}
                >
                  <button
                    data-testid={`board-divider-rename-${divider.id}`}
                    onClick={() => { setEditing(true); setMenuOpen(false); }}
                    style={{ textAlign: "left", padding: "6px 8px", background: "transparent", border: "none", cursor: "pointer" }}
                    type="button"
                  >
                    Rename
                  </button>
                  <div style={{ display: "flex", gap: 4, padding: "4px 8px", flexWrap: "wrap" }}>
                    {HIGHLIGHT_COLORS.map((c) => (
                      <button
                        aria-label={`Set color ${c}`}
                        data-testid={`board-divider-color-${c}-${divider.id}`}
                        key={c}
                        onClick={() => { onRename(divider.id, divider.label, c, divider._bo_version); setMenuOpen(false); }}
                        style={{
                          width: 18, height: 18, borderRadius: 4,
                          border: divider.color === c ? "2px solid #0f172a" : "1px solid #cbd5e1",
                          background: `var(--bo-${c}, ${c})`, cursor: "pointer",
                        }}
                        type="button"
                      />
                    ))}
                    <button
                      aria-label="Clear color"
                      data-testid={`board-divider-color-clear-${divider.id}`}
                      onClick={() => { onRename(divider.id, divider.label, null, divider._bo_version); setMenuOpen(false); }}
                      style={{ width: 18, height: 18, borderRadius: 4, border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}
                      type="button"
                    >×</button>
                  </div>
                  <button
                    data-testid={`board-divider-delete-${divider.id}`}
                    onClick={() => { if (window.confirm(`Delete divider "${divider.label}"?`)) onDelete(divider.id); setMenuOpen(false); }}
                    style={{ textAlign: "left", padding: "6px 8px", color: "#b91c1c", background: "transparent", border: "none", cursor: "pointer" }}
                    type="button"
                  >
                    Delete divider
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}
